/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";
import {
	autoUpdate,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useRole,
	FloatingFocusManager,
	flip
} from "@floating-ui/react";
import mergeRefs from "merge-refs";

import { Maybe } from "@/frameworks/libraries/components";
import { Icon, Loader, TypographyTooltip, Row } from "@/frameworks/designStuff/components";
import { wrapperStyle, layerStyle, selectOptionStyle, selectStyle, subLabelStyle } from "./private/emotion";
import { TextField } from "@/frameworks/designStuff/components";

interface SelectProps extends React.InputHTMLAttributes<HTMLInputElement> {
	value?: string;
	options: {
		label: string;
		value: string;
		meta?: string;
		isSubLabel?: boolean;
	}[];
	label?: string;
	labelIcon?: React.ReactNode;
	rightIcon?: "down" | "more";
	className?: string;
	disabled?: boolean;
	isOutLined?: boolean;
	maxHeight?: UnitString;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	isLoading?: boolean;
	help?: string;
	error?: boolean;
}

function Select({
	isLoading,
	value = "",
	label,
	onChange,
	placeholder,
	disabled,
	maxHeight = "300px",
	labelIcon,
	options,
	error,
	help,
	rightIcon = "down",
	isOutLined,
	...rest
}: SelectProps) {
	const [isOpen, setIsOpen] = useState(false);
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		strategy: "absolute",
		onOpenChange: setIsOpen,
		middleware: [flip({ fallbackAxisSideDirection: "end" }), shift()],
		whileElementsMounted: autoUpdate
	});
	const click = useClick(context);
	const spanRef = useRef<HTMLSpanElement>(null);
	const dismiss = useDismiss(context, {
		outsidePressEvent: "mousedown",
		outsidePress: (event) => {
			if (spanRef.current && event.target instanceof Node && spanRef.current.contains(event.target)) {
				return false;
			}
			return true;
		}
	});
	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
	const theme = useTheme();
	const inputRef = useRef<HTMLInputElement>(null);
	const [v, setV] = useState(value);
	const [width, setWidth] = useState<UnitString>("0px");

	useEffect(() => {
		setV(value);
	}, [value]);

	const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
		const target = e.target as HTMLButtonElement;
		setIsOpen(!isOpen);
		setWidth(`${target.offsetWidth}px`);
	};

	const handleSelectOptionClick = (e: React.MouseEvent<HTMLElement>) => {
		if (!inputRef.current) return;

		const parentTarget = e.currentTarget;
		const target = parentTarget.firstChild as HTMLInputElement;
		if (target) {
			const value = target.value;

			if (value) {
				setV(value);
			}
			setIsOpen(false);
			inputRef.current.setAttribute("value", value || "");

			// BUG : https://github.com/facebook/react/issues/27283
			/*
		inputRef.current.dispatchEvent(
			new InputEvent("change", {
				bubbles: true
			})
		);*/
			if (onChange) {
				onChange({
					target
				} as React.ChangeEvent<HTMLInputElement>);
			}
		}
	};
	if (isLoading) {
		return (
			<div css={wrapperStyle(theme)}>
				<Loader />
			</div>
		);
	}

	return (
		<div {...rest} css={wrapperStyle(theme)}>
			<TextField.Icon
				css={selectStyle(theme, isOutLined || false)}
				{...getReferenceProps()}
				//@ts-ignore
				ref={mergeRefs(inputRef, refs.setReference)}
				value={options.find((option) => option.value === v)?.label || v}
				label={label}
				placeholder={placeholder}
				onClick={handleClick}
				onChange={onChange}
				readOnly
				disabled={disabled}
				labelIcon={labelIcon}
				error={error}
				help={help}
				rightIcon={
					<span
						ref={spanRef}
						css={{ display: "flex", alignItems: "center" }}
						onClick={() => labelIcon && inputRef.current?.click()}
					>
						<Icon name={rightIcon === "down" ? "Down16" : "More24"} size={16} />
					</span>
				}
			/>
			{isOpen && (
				<FloatingFocusManager context={context} modal={false}>
					<ul
						className="popover-layer"
						ref={refs.setFloating}
						{...getFloatingProps()}
						style={floatingStyles}
						css={[layerStyle(width, maxHeight)]}
					>
						{options?.map((option, index) => {
							return (
								<Maybe
									key={`${option.value}-${option.label}-${index}`}
									test={!option?.isSubLabel}
									fallback={
										<li key={`${option.label}-${index}`}>
											<p css={subLabelStyle(theme)}>{option.label}</p>
										</li>
									}
								>
									<li
										key={`${option.value}-${index}`}
										css={selectOptionStyle(theme)}
										onClick={handleSelectOptionClick}
										style={{ maxWidth: "100%" }}
									>
										<input
											type="hidden"
											data-name="select-option"
											value={option.value}
											data-meta={option?.meta || null}
										/>
										<Row css={{ width: "100%" }}>
											<TypographyTooltip.p
												tooltipProps={{ hasIcon: false }}
												componentProps={{
													ellipsis: true,
													maxLine: 1,
													stretch: true,
													style: { display: "block" }
												}}
											>
												{option.label}
											</TypographyTooltip.p>
										</Row>
									</li>
								</Maybe>
							);
						})}
					</ul>
				</FloatingFocusManager>
			)}
		</div>
	);
}

Select.Select = Select;

export default Select;
